import { useState, useEffect, useCallback } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RegrasTab, PainelTab } from "@/pages/ConciliacaoExtras";
import { AnaliseIADialog, VincularBaixadoDialog } from "@/pages/ConciliacaoIA";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Landmark, Upload, CheckCircle2, Undo2, Ban, ArrowLeftRight, Search,
  Plus, RefreshCw, Scale, AlertTriangle, XCircle, Sparkles,
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtData = (d) => (d ? d.split("-").reverse().join("/") : "-");

const CONF_STYLE = {
  alta: "border-l-4 border-l-emerald-500 bg-emerald-50/40",
  media: "border-l-4 border-l-amber-400 bg-amber-50/40",
  tolerancia: "border-l-4 border-l-amber-400 bg-amber-50/40",
};

function HeaderCards({ det }) {
  const imp = det.importacao;
  const dif = det.diferenca;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="conciliacao-header-cards">
      <Card className="border-0 shadow-sm"><CardContent className="p-3">
        <p className="text-[10px] uppercase font-bold text-slate-400">Saldo do Extrato</p>
        <p className="font-mono font-bold text-luvcorp-navy">{det.saldo_extrato != null ? fmtBRL(det.saldo_extrato) : "—"}</p>
      </CardContent></Card>
      <Card className="border-0 shadow-sm"><CardContent className="p-3">
        <p className="text-[10px] uppercase font-bold text-slate-400">Saldo do Sistema</p>
        <p className="font-mono font-bold text-luvcorp-navy">{fmtBRL(det.saldo_sistema)}</p>
      </CardContent></Card>
      <Card className={`border-0 shadow-sm ${dif === 0 ? "bg-emerald-50" : "bg-red-50"}`}><CardContent className="p-3">
        <p className="text-[10px] uppercase font-bold text-slate-500 flex items-center gap-1"><Scale className="h-3 w-3" />Diferença</p>
        <p className={`font-mono font-bold text-lg ${dif === 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="diferenca-valor">
          {dif != null ? fmtBRL(dif) : "—"}
        </p>
      </CardContent></Card>
      <Card className="border-0 shadow-sm"><CardContent className="p-3">
        <p className="text-[10px] uppercase font-bold text-slate-400">Conciliadas / Pendentes</p>
        <p className="font-bold"><span className="text-emerald-600">{imp.linhas_conciliadas}</span> / <span className="text-amber-600">{imp.linhas_pendentes}</span></p>
      </CardContent></Card>
      <Card className="border-0 shadow-sm"><CardContent className="p-3">
        <p className="text-[10px] uppercase font-bold text-slate-400">Ignoradas / Duplicadas</p>
        <p className="font-bold text-slate-600">{imp.linhas_ignoradas} / {imp.linhas_duplicadas || 0}</p>
      </CardContent></Card>
    </div>
  );
}

function SugestaoLancamento({ lanc, cats, ccs, obras, edits, setEdit, showAjuste, diferenca }) {
  if (!lanc) return null;
  return (
    <div className="space-y-1.5 text-sm">
      <p className="font-medium text-luvcorp-navy leading-tight">{lanc.descricao || "—"}</p>
      <p className="text-xs text-slate-500">
        venc. {fmtData(lanc.data_vencimento)} · <span className="font-mono font-semibold">{fmtBRL(lanc.valor)}</span>
        {lanc.parcela ? ` · parc. ${lanc.parcela}` : ""}
        {lanc.baixa_sem_conta && <span className="ml-1 text-amber-600">· pago sem conta</span>}
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-1.5">
        <NativeSelect value={edits.categoria_id ?? lanc.categoria_id ?? ""} onValueChange={(v) => setEdit("categoria_id", v)}
          placeholder="Categoria" options={[{ value: "", label: "Categoria…" }, ...cats.map(c => ({ value: c.id, label: c.nome }))]} className="h-8 text-xs" />
        <NativeSelect value={edits.centro_custo_id ?? lanc.centro_custo_id ?? ""} onValueChange={(v) => setEdit("centro_custo_id", v)}
          placeholder="Centro de custo" options={[{ value: "", label: "C. Custo…" }, ...ccs.map(c => ({ value: c.id, label: c.nome }))]} className="h-8 text-xs" />
        <NativeSelect value={edits.contrato_id ?? lanc.contrato_id ?? ""} onValueChange={(v) => setEdit("contrato_id", v)}
          placeholder="Obra" options={[{ value: "", label: "Obra…" }, ...obras.map(o => ({ value: o.id, label: `${o.numero_proposta || ""} ${o.titulo || o.cliente_nome || ""}`.trim().slice(0, 40) }))]} className="h-8 text-xs" />
      </div>
      {showAjuste && (
        <div className="grid grid-cols-3 gap-1.5 p-1.5 rounded bg-amber-50 border border-amber-200">
          <div><Label className="text-[10px]">Juros</Label>
            <Input type="number" step="0.01" min="0" className="h-7 text-xs" value={edits.juros ?? ""} onChange={(e) => setEdit("juros", e.target.value)} /></div>
          <div><Label className="text-[10px]">Multa</Label>
            <Input type="number" step="0.01" min="0" className="h-7 text-xs" value={edits.multa ?? ""} onChange={(e) => setEdit("multa", e.target.value)} /></div>
          <div><Label className="text-[10px]">Desconto</Label>
            <Input type="number" step="0.01" min="0" className="h-7 text-xs" value={edits.desconto ?? ""} onChange={(e) => setEdit("desconto", e.target.value)} /></div>
          {diferenca != null && <p className="col-span-3 text-[10px] text-amber-700">Diferença extrato − lançamento: {fmtBRL(diferenca)}</p>}
        </div>
      )}
    </div>
  );
}

export default function ConciliacaoPage() {
  const [bancos, setBancos] = useState([]);
  const [importacoes, setImportacoes] = useState([]);
  const [bancoSel, setBancoSel] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [importando, setImportando] = useState(false);
  const [impSel, setImpSel] = useState("");
  const [det, setDet] = useState(null);
  const [loadingDet, setLoadingDet] = useState(false);
  const [cats, setCats] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [obras, setObras] = useState([]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);
  // filtros
  const [fStatus, setFStatus] = useState("pendente");
  const [fTipo, setFTipo] = useState("all");
  const [fMin, setFMin] = useState(""); const [fMax, setFMax] = useState("");
  // dialogs
  const [picker, setPicker] = useState(null);       // linha para "Trocar"
  const [criarLinha, setCriarLinha] = useState(null); // linha para "Criar lançamento"
  const [regraPrompt, setRegraPrompt] = useState(null); // {linha, acao, categoria_id...}
  const [iaOpen, setIaOpen] = useState(false);
  const [iaLoading, setIaLoading] = useState(false);
  const [vincBaixado, setVincBaixado] = useState(null); // {linha, alerta}
  const [reanalisando, setReanalisando] = useState(false);

  const fetchBase = useCallback(async () => {
    try {
      const [b, i, c1, c2, ob] = await Promise.all([
        axios.get(`${API}/bancos?ativo=true`),
        axios.get(`${API}/conciliacao/importacoes`),
        axios.get(`${API}/categorias`),
        axios.get(`${API}/centros-custo?ativo=true`),
        axios.get(`${API}/conciliacao/contratos-lite`),
      ]);
      setBancos((b.data || []).filter(x => x.conciliacao_ativa !== false));
      setImportacoes(i.data || []);
      setCats(c1.data || []); setCcs(c2.data || []); setObras(ob.data || []);
    } catch { toast.error("Erro ao carregar dados da conciliação"); }
  }, []);
  useEffect(() => { fetchBase(); }, [fetchBase]);

  const fetchDetalhe = useCallback(async (id) => {
    if (!id) { setDet(null); return; }
    setLoadingDet(true);
    try {
      const r = await axios.get(`${API}/conciliacao/importacoes/${id}`);
      setDet(r.data);
    } catch { toast.error("Erro ao carregar importação"); }
    finally { setLoadingDet(false); }
  }, []);
  useEffect(() => { fetchDetalhe(impSel); }, [impSel, fetchDetalhe]);

  const handleImportar = async () => {
    if (!bancoSel) { toast.error("Selecione a conta bancária"); return; }
    if (!arquivo) { toast.error("Selecione o arquivo OFX"); return; }
    setImportando(true);
    try {
      const fd = new FormData();
      fd.append("banco_id", bancoSel);
      fd.append("file", arquivo);
      const r = await axios.post(`${API}/conciliacao/importar`, fd);
      const d = r.data;
      toast.success(`Importado: ${d.linhas_novas} linhas novas, ${d.duplicadas_ignoradas} duplicadas, ${d.sugestoes_alta_confianca} de alta confiança`);
      (d.avisos || []).forEach(a => toast.warning(a, { duration: 8000 }));
      setArquivo(null);
      await fetchBase();
      setImpSel(d.importacao.id);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao importar extrato"));
    } finally { setImportando(false); }
  };

  const setEditFor = (linhaId, campo, valor) =>
    setEdits(prev => ({ ...prev, [linhaId]: { ...(prev[linhaId] || {}), [campo]: valor } }));

  const conciliar = async (linha, lancamentoId) => {
    const e = edits[linha.id] || {};
    const rc = linha.regra_classificacao || {};
    setBusy(linha.id);
    try {
      const body = { lancamento_id: lancamentoId };
      ["categoria_id", "centro_custo_id", "contrato_id"].forEach(f => {
        const v = e[f] !== undefined ? e[f] : rc[f];
        if (v) body[f] = v;
      });
      ["juros", "multa", "desconto"].forEach(f => { if (e[f] !== undefined && e[f] !== "") body[f] = parseFloat(e[f]); });
      await axios.post(`${API}/conciliacao/linhas/${linha.id}/conciliar`, body);
      toast.success("Conciliada — lançamento baixado");
      // Oferece memorizar regra quando o usuario classificou manualmente
      if (e.categoria_id && !linha.regra_aplicada_id) {
        setRegraPrompt({ linha, acao: "sugerir_categoria", categoria_id: e.categoria_id, centro_custo_id: e.centro_custo_id, contrato_id: e.contrato_id });
      }
      fetchDetalhe(impSel);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao conciliar")); }
    finally { setBusy(null); }
  };

  const conciliarSoma = async (linha, ids) => {
    setBusy(linha.id);
    try {
      const r = await axios.post(`${API}/conciliacao/linhas/${linha.id}/conciliar-multiplos`, { lancamento_ids: ids });
      toast.success(r.data.message);
      fetchDetalhe(impSel);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao conciliar por soma")); }
    finally { setBusy(null); }
  };

  const acaoLinha = async (linha, acao) => {
    setBusy(linha.id);
    try {
      await axios.post(`${API}/conciliacao/linhas/${linha.id}/${acao}`, {});
      toast.success(acao === "desfazer" ? "Desfeito — estado anterior restaurado" : "Linha atualizada");
      if ((acao === "ignorar" || acao === "marcar-transferencia") && !linha.regra_aplicada_id) {
        setRegraPrompt({ linha, acao: acao === "ignorar" ? "ignorar" : "transferencia" });
      }
      fetchDetalhe(impSel);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro na ação")); }
    finally { setBusy(null); }
  };

  const conciliarLote = async () => {
    setBusy("lote");
    try {
      const r = await axios.post(`${API}/conciliacao/conciliar-lote`, { importacao_id: impSel });
      toast.success(`${r.data.conciliadas} linha(s) de alta confiança conciliadas${r.data.falhas.length ? ` · ${r.data.falhas.length} falha(s)` : ""}`);
      fetchDetalhe(impSel);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro no lote")); }
    finally { setBusy(null); }
  };

  const desfazerLote = async () => {
    setBusy("lote");
    try {
      const r = await axios.post(`${API}/conciliacao/importacoes/${impSel}/desfazer-lote`);
      toast.success(`${r.data.desfeitas} conciliação(ões) desfeitas`);
      fetchDetalhe(impSel);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao desfazer lote")); }
    finally { setBusy(null); }
  };

  const reanalisar = async () => {
    setReanalisando(true);
    try {
      const r = await axios.post(`${API}/conciliacao/importacoes/${impSel}/reanalisar`);
      toast.success(r.data.message);
      fetchDetalhe(impSel);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao reanalisar")); }
    finally { setReanalisando(false); }
  };

  const analisarIA = async () => {
    setIaLoading(true); setIaOpen(true);
    try {
      const r = await axios.post(`${API}/conciliacao/importacoes/${impSel}/analisar-ia`);
      setDet(d => (d ? { ...d, importacao: { ...d.importacao, analise_ia: r.data } } : d));
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro na análise de IA"));
      setIaOpen(false);
    } finally { setIaLoading(false); }
  };

  const vincularBaixado = async (ajustarData) => {
    const { linha, alerta } = vincBaixado;
    setVincBaixado(null);
    setBusy(linha.id);
    try {
      await axios.post(`${API}/conciliacao/linhas/${linha.id}/conciliar`, {
        lancamento_id: alerta.lancamento_id, permitir_baixado: true, ajustar_data_baixa: ajustarData,
      });
      toast.success(ajustarData ? "Vinculado — data da baixa corrigida para a data do extrato" : "Vinculado — data original da baixa mantida");
      fetchDetalhe(impSel);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao vincular")); }
    finally { setBusy(null); }
  };

  const linhasFiltradas = (det?.linhas || []).filter(l => {
    if (fStatus !== "all" && l.status !== fStatus) return false;
    if (fTipo === "entrada" && l.tipo !== "credito") return false;
    if (fTipo === "saida" && l.tipo !== "debito") return false;
    const v = Math.abs(l.valor || 0);
    if (fMin && v < parseFloat(fMin)) return false;
    if (fMax && v > parseFloat(fMax)) return false;
    return true;
  });
  const altaCount = (det?.linhas || []).filter(l => l.status === "pendente" && l.sugestao_confianca === "alta").length;

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="conciliacao-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-luvcorp-navy flex items-center gap-2">
            <Scale className="h-6 w-6 text-luvcorp-gold" />Conciliação Bancária
          </h1>
          <p className="text-slate-500">Importe o extrato OFX do banco e concilie com os lançamentos do sistema</p>
        </div>

        <Tabs defaultValue="conciliar">
          <TabsList data-testid="tabs-conciliacao">
            <TabsTrigger value="conciliar" data-testid="tab-conciliar">Conciliar</TabsTrigger>
            <TabsTrigger value="regras" data-testid="tab-regras">Regras</TabsTrigger>
            <TabsTrigger value="painel" data-testid="tab-painel">Painel</TabsTrigger>
          </TabsList>

          <TabsContent value="regras" className="mt-4"><RegrasTab /></TabsContent>
          <TabsContent value="painel" className="mt-4"><PainelTab /></TabsContent>
          <TabsContent value="conciliar" className="mt-4 space-y-5">

        {/* Importar */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex flex-col md:flex-row gap-3 md:items-end">
            <div className="space-y-1 md:w-64">
              <Label className="text-xs">Conta bancária</Label>
              <NativeSelect value={bancoSel} onValueChange={setBancoSel} placeholder="Selecione…"
                options={bancos.map(b => ({ value: b.id, label: `${b.nome}${b.agencia ? ` (Ag ${b.agencia})` : ""}` }))}
                data-testid="select-banco-conciliacao" />
            </div>
            <div className="space-y-1 flex-1">
              <Label className="text-xs">Arquivo OFX (Santander/Sicredi — exporte no internet banking)</Label>
              <Input type="file" accept=".ofx,.OFX" onChange={(e) => setArquivo(e.target.files?.[0] || null)} data-testid="input-arquivo-ofx" />
            </div>
            <Button onClick={handleImportar} disabled={importando || !bancoSel || !arquivo}
              className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" data-testid="btn-importar-ofx">
              <Upload className="h-4 w-4 mr-2" />{importando ? "Importando…" : "Importar OFX"}
            </Button>
            <div className="space-y-1 md:w-72">
              <Label className="text-xs">Importações anteriores</Label>
              <NativeSelect value={impSel} onValueChange={setImpSel} placeholder="Abrir importação…"
                options={[{ value: "", label: "— Selecionar —" }, ...importacoes.map(i => ({
                  value: i.id,
                  label: `${(i.created_at || "").slice(0, 10)} · ${i.banco_nome || ""} · ${i.arquivo_nome || ""} (${i.status})`.slice(0, 60),
                }))]}
                data-testid="select-importacao" />
            </div>
          </CardContent>
        </Card>

        {loadingDet && <p className="text-slate-500 text-sm">Carregando importação…</p>}

        {det && !loadingDet && (
          <>
            <HeaderCards det={det} />

            {/* Barra de acoes + filtros */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-3 flex flex-wrap gap-2 items-center">
                <Button size="sm" onClick={conciliarLote} disabled={busy || altaCount === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-conciliar-lote">
                  <CheckCircle2 className="h-4 w-4 mr-1" />Conciliar todas de alta confiança ({altaCount})
                </Button>
                <Button size="sm" variant="outline" onClick={desfazerLote} disabled={busy || !det.importacao.linhas_conciliadas}
                  data-testid="btn-desfazer-lote">
                  <Undo2 className="h-4 w-4 mr-1" />Desfazer lote
                </Button>
                <Button size="sm" variant="outline" onClick={reanalisar} disabled={busy || reanalisando}
                  data-testid="btn-reanalisar">
                  <RefreshCw className={`h-4 w-4 mr-1 ${reanalisando ? "animate-spin" : ""}`} />{reanalisando ? "Reanalisando…" : "Reanalisar sugestões"}
                </Button>
                <Button size="sm" onClick={() => { if (det.importacao.analise_ia) { setIaOpen(true); } else { analisarIA(); } }}
                  disabled={iaLoading} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="btn-analisar-ia">
                  <Sparkles className="h-4 w-4 mr-1" />Analisar com IA
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-300"
                  onClick={() => acaoImportacaoCancelar(impSel, fetchBase, fetchDetalhe, setImpSel)} disabled={busy}
                  data-testid="btn-cancelar-importacao">
                  <XCircle className="h-4 w-4 mr-1" />Cancelar importação
                </Button>
                <div className="flex-1" />
                <NativeSelect value={fStatus} onValueChange={setFStatus} className="w-[150px] h-9"
                  options={[{ value: "pendente", label: "Só pendentes" }, { value: "conciliada", label: "Conciliadas" },
                  { value: "ignorada", label: "Ignoradas" }, { value: "transferencia_interna", label: "Transferências" },
                  { value: "all", label: "Todas" }]} data-testid="filtro-status-linha" />
                <NativeSelect value={fTipo} onValueChange={setFTipo} className="w-[130px] h-9"
                  options={[{ value: "all", label: "Entradas+Saídas" }, { value: "entrada", label: "Só entradas" }, { value: "saida", label: "Só saídas" }]} />
                <Input type="number" placeholder="Valor mín" className="w-24 h-9" value={fMin} onChange={e => setFMin(e.target.value)} />
                <Input type="number" placeholder="Valor máx" className="w-24 h-9" value={fMax} onChange={e => setFMax(e.target.value)} />
                <Button size="sm" variant="ghost" onClick={() => fetchDetalhe(impSel)}><RefreshCw className="h-4 w-4" /></Button>
              </CardContent>
            </Card>

            {/* Cabecalho das colunas (desktop) */}
            <div className="hidden md:grid grid-cols-[1fr_1.2fr_190px] gap-3 px-3 text-[10px] uppercase font-bold text-slate-400">
              <span>Extrato ({det.banco?.nome} · {fmtData(det.importacao.data_inicio)} a {fmtData(det.importacao.data_fim)})</span>
              <span>Sistema</span><span>Ação</span>
            </div>

            {/* Linhas */}
            <div className="space-y-2" data-testid="lista-linhas-extrato">
              {linhasFiltradas.length === 0 && (
                <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center text-slate-400">
                  Nenhuma linha para os filtros selecionados.
                </CardContent></Card>
              )}
              {linhasFiltradas.map(linha => {
                const sug = linha.sugestao_lancamento;
                const conciliada = linha.status === "conciliada";
                const marcada = ["ignorada", "transferencia_interna"].includes(linha.status);
                const style = conciliada ? "border-l-4 border-l-blue-400 bg-blue-50/30"
                  : marcada ? "border-l-4 border-l-slate-300 bg-slate-50 opacity-70"
                  : CONF_STYLE[linha.sugestao_confianca]
                  || (linha.alerta_ja_baixado ? "border-l-4 border-l-orange-400 bg-orange-50/30" : "border-l-4 border-l-slate-200");
                return (
                  <Card key={linha.id} className={`border-0 shadow-sm ${style}`} data-testid={`linha-extrato-${linha.id}`}>
                    <CardContent className="p-3 grid grid-cols-1 md:grid-cols-[1fr_1.2fr_190px] gap-3 items-start">
                      {/* EXTRATO */}
                      <div className="text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">{fmtData(linha.data)}</span>
                          <span className={`font-mono font-bold ${linha.tipo === "credito" ? "text-emerald-700" : "text-red-700"}`}>
                            {linha.tipo === "credito" ? "+" : "−"} {fmtBRL(Math.abs(linha.valor))}
                          </span>
                          {linha.sugestao_confianca === "alta" && linha.status === "pendente" &&
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">alta confiança</Badge>}
                          {["media", "tolerancia"].includes(linha.sugestao_confianca) && linha.status === "pendente" &&
                            <Badge className="bg-amber-100 text-amber-700 text-[10px]">sugestão</Badge>}
                          {linha.regra_aplicada_id &&
                            <Badge className="bg-blue-100 text-blue-700 text-[10px]" title="Aplicada por regra memorizada">regra</Badge>}
                          {linha.alerta_duplicidade && linha.status === "pendente" &&
                            <Badge className="bg-orange-100 text-orange-700 text-[10px]"
                              title="Este valor aparece mais de uma vez neste extrato — confira possível duplicidade de pagamento/recebimento"
                              data-testid={`badge-duplicidade-${linha.id}`}>
                              <AlertTriangle className="h-3 w-3 mr-0.5" />possível duplicidade ({linha.alerta_duplicidade.ocorrencias}x)
                            </Badge>}
                          {conciliada && <Badge className="bg-blue-100 text-blue-700 text-[10px]">conciliada</Badge>}
                          {marcada && <Badge className="bg-slate-200 text-slate-600 text-[10px]">{linha.status === "ignorada" ? "ignorada" : "transferência"}</Badge>}
                        </div>
                        <p className="text-slate-700 mt-1 leading-tight break-words">{linha.descricao_original || "—"}</p>
                        {linha.documento && <p className="text-[10px] text-slate-400 font-mono">doc {linha.documento}</p>}
                      </div>

                      {/* SISTEMA */}
                      <div className="hidden md:block">
                        {conciliada && linha.lancamento && (
                          <div className="text-sm">
                            <p className="font-medium text-luvcorp-navy">{linha.lancamento.descricao}</p>
                            <p className="text-xs text-slate-500">venc. {fmtData(linha.lancamento.data_vencimento)} · {fmtBRL(linha.lancamento.valor)}</p>
                          </div>
                        )}
                        {conciliada && linha.lancamentos && (
                          <div className="text-sm space-y-1">
                            <p className="text-xs font-semibold text-blue-700">Conciliada com {linha.lancamentos.length} lançamentos (soma):</p>
                            {linha.lancamentos.map(lc => (
                              <p key={lc.id} className="text-xs text-slate-600">• {lc.descricao} — {fmtBRL(lc.valor)}</p>
                            ))}
                          </div>
                        )}
                        {!conciliada && !marcada && linha.sugestao_lancamentos && (
                          <div className="text-sm space-y-1">
                            <Badge className="bg-purple-100 text-purple-700 text-[10px]">soma de 2 lançamentos</Badge>
                            {linha.sugestao_lancamentos.map(lc => (
                              <p key={lc.id} className="text-xs text-slate-700">• {lc.descricao} — <span className="font-mono">{fmtBRL(lc.valor)}</span> (venc. {fmtData(lc.data_vencimento)})</p>
                            ))}
                          </div>
                        )}
                        {!conciliada && !marcada && sug && (
                          <SugestaoLancamento lanc={sug} cats={cats} ccs={ccs} obras={obras}
                            edits={{ ...(linha.regra_classificacao || {}), ...(edits[linha.id] || {}) }} setEdit={(f, v) => setEditFor(linha.id, f, v)}
                            showAjuste={linha.sugestao_confianca === "tolerancia"} diferenca={linha.sugestao_diferenca} />
                        )}
                        {!conciliada && !marcada && !sug && !linha.sugestao_lancamentos && linha.alerta_ja_baixado && (
                          <div className="p-2.5 rounded-lg bg-orange-50 border border-orange-200 text-xs space-y-1" data-testid={`alerta-ja-baixado-${linha.id}`}>
                            <p className="font-semibold text-orange-800 flex items-center gap-1">
                              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />Já existe lançamento BAIXADO neste valor
                            </p>
                            <p className="text-slate-700 leading-tight">{linha.alerta_ja_baixado.descricao}</p>
                            <p className="text-slate-600">
                              Baixado em <b>{fmtData(linha.alerta_ja_baixado.data_pagamento)}</b> · extrato em <b>{fmtData(linha.data)}</b>
                              {!linha.alerta_ja_baixado.mesma_data && <span className="text-red-600 font-semibold"> — datas divergentes</span>}
                              {linha.alerta_ja_baixado.ocorrencias > 1 && ` · ${linha.alerta_ja_baixado.ocorrencias} baixados neste valor`}
                            </p>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-orange-300 text-orange-800 hover:bg-orange-100"
                              onClick={() => setVincBaixado({ linha, alerta: linha.alerta_ja_baixado })}
                              data-testid={`btn-vincular-baixado-${linha.id}`}>
                              Vincular a este baixado…
                            </Button>
                          </div>
                        )}
                        {!conciliada && !marcada && !sug && !linha.sugestao_lancamentos && !linha.alerta_ja_baixado && (
                          <p className="text-xs text-slate-400 italic pt-2">Sem correspondência no sistema — busque um lançamento ou crie a partir do extrato.</p>
                        )}
                      </div>

                      {/* ACAO */}
                      <div className="flex flex-wrap md:flex-col gap-1.5">
                        {linha.status === "pendente" && sug && (
                          <Button size="sm" onClick={() => conciliar(linha, sug.id)} disabled={busy === linha.id}
                            className={linha.sugestao_confianca === "alta" ? "bg-emerald-600 hover:bg-emerald-700 text-white w-full" : "bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white w-full"}
                            data-testid={`btn-conciliar-${linha.id}`}>
                            <CheckCircle2 className="h-4 w-4 mr-1" />Baixar
                          </Button>
                        )}
                        {linha.status === "pendente" && !sug && linha.sugestao_lancamento_ids && (
                          <Button size="sm" onClick={() => conciliarSoma(linha, linha.sugestao_lancamento_ids)} disabled={busy === linha.id}
                            className="bg-purple-600 hover:bg-purple-700 text-white w-full" data-testid={`btn-conciliar-soma-${linha.id}`}>
                            <CheckCircle2 className="h-4 w-4 mr-1" />Baixar os 2
                          </Button>
                        )}
                        {linha.status === "pendente" && (
                          <>
                            <Button size="sm" variant="outline" className="w-full" onClick={() => setPicker(linha)} data-testid={`btn-trocar-${linha.id}`}>
                              <Search className="h-4 w-4 mr-1" />{sug ? "Trocar" : "Buscar"}
                            </Button>
                            <Button size="sm" variant="outline" className="w-full" onClick={() => setCriarLinha(linha)} data-testid={`btn-criar-lanc-${linha.id}`}>
                              <Plus className="h-4 w-4 mr-1" />Criar lançamento
                            </Button>
                            <div className="flex gap-1.5 w-full">
                              <Button size="sm" variant="ghost" className="flex-1 text-slate-500" title="Ignorar linha"
                                onClick={() => acaoLinha(linha, "ignorar")} data-testid={`btn-ignorar-${linha.id}`}><Ban className="h-4 w-4" /></Button>
                              <Button size="sm" variant="ghost" className="flex-1 text-indigo-600" title="Marcar como transferência interna"
                                onClick={() => acaoLinha(linha, "marcar-transferencia")} data-testid={`btn-transf-${linha.id}`}><ArrowLeftRight className="h-4 w-4" /></Button>
                            </div>
                          </>
                        )}
                        {(conciliada || marcada) && (
                          <Button size="sm" variant="outline" className="w-full" onClick={() => acaoLinha(linha, "desfazer")}
                            disabled={busy === linha.id} data-testid={`btn-desfazer-${linha.id}`}>
                            <Undo2 className="h-4 w-4 mr-1" />Desfazer
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {!det && !loadingDet && (
          <Card className="border-0 shadow-sm"><CardContent className="p-10 text-center text-slate-400">
            <Landmark className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            Importe um extrato OFX ou abra uma importação anterior para começar.
          </CardContent></Card>
        )}

          </TabsContent>
        </Tabs>

        <PickerDialog linha={picker} onClose={() => setPicker(null)}
          onEscolher={(lanc) => { const l = picker; setPicker(null); conciliar(l, lanc.id); }}
          onEscolherMultiplos={(ids) => { const l = picker; setPicker(null); conciliarSoma(l, ids); }} />
        <CriarLancamentoDialog linha={criarLinha} cats={cats} ccs={ccs} obras={obras}
          onClose={() => setCriarLinha(null)}
          onCriado={() => { setCriarLinha(null); fetchDetalhe(impSel); }} />
        <RegraPromptDialog prompt={regraPrompt} bancos={bancos} onClose={() => setRegraPrompt(null)} />
        <AnaliseIADialog open={iaOpen} onClose={() => setIaOpen(false)} loading={iaLoading}
          analise={det?.importacao?.analise_ia} onAnalisar={analisarIA} />
        <VincularBaixadoDialog data={vincBaixado} onClose={() => setVincBaixado(null)} onVincular={vincularBaixado} />
      </div>
    </MainLayout>
  );
}

async function acaoImportacaoCancelar(impSel, fetchBase, fetchDetalhe, setImpSel) {
  if (!window.confirm("Cancelar esta importação? Conciliações feitas por ela serão desfeitas e as linhas removidas (o mesmo arquivo poderá ser reimportado).")) return;
  try {
    const r = await axios.post(`${API}/conciliacao/importacoes/${impSel}/cancelar`);
    toast.success(`Importação cancelada (${r.data.conciliacoes_desfeitas} conciliações desfeitas)`);
    await fetchBase();
    setImpSel("");
  } catch (err) { toast.error(extractErrorMessage(err, "Erro ao cancelar")); }
}

function PickerDialog({ linha, onClose, onEscolher, onEscolherMultiplos }) {
  const [busca, setBusca] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marcados, setMarcados] = useState([]);
  useEffect(() => {
    if (!linha) return;
    setBusca(""); setItems([]); setMarcados([]);
    const tipo = linha.tipo === "credito" ? "receita" : "despesa";
    setLoading(true);
    axios.get(`${API}/conciliacao/lancamentos-candidatos?tipo=${tipo}&valor=${Math.abs(linha.valor)}`)
      .then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [linha]);
  const buscar = () => {
    const tipo = linha.tipo === "credito" ? "receita" : "despesa";
    setLoading(true);
    axios.get(`${API}/conciliacao/lancamentos-candidatos?tipo=${tipo}&valor=${Math.abs(linha.valor)}&busca=${encodeURIComponent(busca)}`)
      .then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  return (
    <Dialog open={!!linha} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dialog-picker-lancamento">
        <DialogHeader>
          <DialogTitle>Escolher lançamento</DialogTitle>
          <DialogDescription className="text-xs">
            Linha do extrato: {linha && `${fmtData(linha.data)} · ${fmtBRL(Math.abs(linha.valor))} · ${(linha.descricao_original || "").slice(0, 50)}`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input placeholder="Buscar por descrição…" value={busca} onChange={e => setBusca(e.target.value)}
            onKeyDown={e => e.key === "Enter" && buscar()} data-testid="input-busca-picker" />
          <Button variant="outline" onClick={buscar}><Search className="h-4 w-4" /></Button>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1.5">
          {loading && <p className="text-sm text-slate-400 p-3">Buscando…</p>}
          {!loading && items.length === 0 && <p className="text-sm text-slate-400 p-3">Nenhum lançamento pendente compatível.</p>}
          {items.map(l => (
            <div key={l.id} className="flex items-center gap-2 p-2.5 rounded-lg border hover:border-luvcorp-gold hover:bg-amber-50/40 transition">
              <input type="checkbox" checked={marcados.includes(l.id)}
                onChange={(e) => setMarcados(m => e.target.checked ? [...m, l.id] : m.filter(x => x !== l.id))}
                title="Marcar para conciliação por soma" data-testid={`picker-check-${l.id}`} />
              <button onClick={() => onEscolher(l)} className="flex-1 text-left" data-testid={`picker-item-${l.id}`}>
                <div className="flex justify-between gap-2">
                  <span className="text-sm font-medium text-luvcorp-navy truncate">{l.descricao || "—"}</span>
                  <span className="font-mono text-sm font-semibold whitespace-nowrap">{fmtBRL(l.valor)}</span>
                </div>
                <p className="text-[11px] text-slate-500">
                  venc. {fmtData(l.data_vencimento)}{l.parcela ? ` · parc. ${l.parcela}` : ""}
                  {l.baixa_sem_conta ? " · pago sem conta" : ""}{l.fornecedor_nome || l.cliente_nome ? ` · ${l.fornecedor_nome || l.cliente_nome}` : ""}
                </p>
              </button>
            </div>
          ))}
        </div>
        {marcados.length >= 2 && linha && (() => {
          const soma = items.filter(i => marcados.includes(i.id)).reduce((a, i) => a + (i.valor || 0), 0);
          const fecha = Math.abs(soma - Math.abs(linha.valor)) <= 0.05;
          return (
            <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-purple-50 border border-purple-200">
              <p className="text-xs">
                <b>{marcados.length}</b> marcados · soma <b className="font-mono">{fmtBRL(soma)}</b> / linha <b className="font-mono">{fmtBRL(Math.abs(linha.valor))}</b>
                {!fecha && <span className="text-red-600"> — não fecha (tolerância R$ 0,05)</span>}
              </p>
              <Button size="sm" disabled={!fecha} onClick={() => onEscolherMultiplos(marcados)}
                className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="btn-conciliar-marcados">
                Conciliar {marcados.length} (soma)
              </Button>
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}

function CriarLancamentoDialog({ linha, cats, ccs, obras, onClose, onCriado }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (linha) setForm({ descricao: linha.descricao_original || "" }); }, [linha]);
  const criar = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/conciliacao/linhas/${linha.id}/criar-lancamento`, form);
      toast.success("Lançamento criado e conciliado");
      onCriado();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao criar lançamento")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!linha} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-criar-lancamento">
        <DialogHeader>
          <DialogTitle>Criar lançamento do extrato</DialogTitle>
          <DialogDescription className="text-xs">
            {linha && `${fmtData(linha.data)} · ${linha.tipo === "credito" ? "Receita" : "Despesa"} · ${fmtBRL(Math.abs(linha.valor))} — valor e data vêm do extrato e o lançamento já nasce baixado nesta conta.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <div><Label className="text-xs">Descrição</Label>
            <Input value={form.descricao || ""} onChange={e => setForm({ ...form, descricao: e.target.value })} data-testid="input-criar-descricao" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Categoria</Label>
              <NativeSelect value={form.categoria_id || ""} onValueChange={v => setForm({ ...form, categoria_id: v })}
                options={[{ value: "", label: "—" }, ...cats.map(c => ({ value: c.id, label: c.nome }))]} /></div>
            <div><Label className="text-xs">Centro de custo</Label>
              <NativeSelect value={form.centro_custo_id || ""} onValueChange={v => setForm({ ...form, centro_custo_id: v })}
                options={[{ value: "", label: "—" }, ...ccs.map(c => ({ value: c.id, label: c.nome }))]} /></div>
          </div>
          <div><Label className="text-xs">Obra (contrato)</Label>
            <NativeSelect value={form.contrato_id || ""} onValueChange={v => setForm({ ...form, contrato_id: v })}
              options={[{ value: "", label: "—" }, ...obras.map(o => ({ value: o.id, label: `${o.numero_proposta || ""} ${o.titulo || o.cliente_nome || ""}`.trim().slice(0, 45) }))]} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={criar} disabled={saving} className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" data-testid="btn-confirmar-criar-lanc">
            {saving ? "Criando…" : "Criar e conciliar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACAO_TXT = { ignorar: "IGNORAR", transferencia: "marcar como TRANSFERÊNCIA", sugerir_categoria: "CLASSIFICAR igual" };

function RegraPromptDialog({ prompt, bancos, onClose }) {
  const [padrao, setPadrao] = useState("");
  const [escopo, setEscopo] = useState("conta");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (prompt) { setPadrao(prompt.linha.descricao_normalizada || ""); setEscopo("conta"); } }, [prompt]);
  if (!prompt) return null;
  const salvar = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/conciliacao/regras`, {
        padrao_descricao: padrao,
        tipo: prompt.linha.tipo,
        acao: prompt.acao,
        banco_id: escopo === "conta" ? prompt.linha.banco_id : null,
        categoria_id: prompt.categoria_id || null,
        centro_custo_id: prompt.centro_custo_id || null,
        contrato_id: prompt.contrato_id || null,
        criada_de_linha_id: prompt.linha.id,
      });
      toast.success("Regra memorizada — será aplicada nas próximas importações");
      onClose();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao criar regra")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!prompt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-regra-prompt">
        <DialogHeader>
          <DialogTitle>Aplicar sempre?</DialogTitle>
          <DialogDescription className="text-xs">
            Nas próximas importações, {ACAO_TXT[prompt.acao]} automaticamente as linhas cuja descrição contenha o padrão abaixo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <div>
            <Label className="text-xs">Padrão (texto normalizado — edite para deixar mais genérico)</Label>
            <Input value={padrao} onChange={(e) => setPadrao(e.target.value)} className="font-mono text-xs" data-testid="input-padrao-regra" />
          </div>
          <div>
            <Label className="text-xs">Escopo</Label>
            <NativeSelect value={escopo} onValueChange={setEscopo}
              options={[
                { value: "conta", label: `Só esta conta (${bancos.find(b => b.id === prompt.linha.banco_id)?.nome || "conta"})` },
                { value: "todas", label: "Todas as contas" },
              ]} data-testid="select-escopo-regra" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="btn-nao-criar-regra">Só desta vez</Button>
          <Button onClick={salvar} disabled={saving || padrao.trim().length < 4}
            className="bg-luvcorp-navy hover:bg-luvcorp-navy/90 text-white" data-testid="btn-criar-regra">
            {saving ? "Salvando…" : "Criar regra"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
